import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material"
import { useAppSelector } from "hooks"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { hiabColors } from "theme"
import { LessonType } from "types/lessonTypes"
import { CompleteStatus, type ITrainingReport } from "types/trainingReportTypes"
import ChallengeIcon from "../../images/icon_challenge.svg"
import ExamIcon from "../../images/icon_exam.svg"
import LessonIcon from "../../images/icon_lesson.svg"
import PracticeIcon from "../../images/icon_practice.svg"

type Props = {
  trainingReports: Array<ITrainingReport>
}

export function TrainingReportList({ trainingReports }: Props) {
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const lessons = useAppSelector((state) => state.lesson.lessons)
  const selectedUser = useAppSelector((state) => state.users.selectedUser)

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getLessonName = (lessonId: string): string => {
    var lesson = lessons.find((x) => x.id === lessonId)

    if (lesson === undefined) {
      return "Undefined Lesson"
    }

    return lesson.name
  }

  const getCraneParameter = (trainingReport: ITrainingReport): string => {
    if (trainingReport.parameters === undefined) {
      return "-"
    }

    const craneParameter = trainingReport.parameters.find((x) => x.name === "crane")

    if (craneParameter) {
      return craneParameter.value
    }

    return "-"
  }

  const getToolParameter = (trainingReport: ITrainingReport): string => {
    if (trainingReport.parameters === undefined) {
      return "-"
    }

    const toolParameter = trainingReport.parameters.find((x) => x.name === "tool")

    if (toolParameter) {
      return toolParameter.value
    }

    return "-"
  }

  const getMinutesBetweenDates = (startDate: Date, endDate: Date): number => {
    const msBetween = endDate.getTime() - startDate.getTime()
    const seconds = Math.floor(msBetween / 1000)
    const minutes = Math.floor(seconds / 60)

    return minutes
  }

  const getIcon = (lessonId: string) => {
    var type = lessons.find((x) => x.id === lessonId)?.type
    switch (type) {
      case LessonType.Challenge:
        return <ChallengeIcon fill={hiabColors.greyPrimary} height={"2rem"} />
      case LessonType.Exam:
        return <ExamIcon fill={hiabColors.greyPrimary} height={"2rem"} />
      case LessonType.Lesson:
        return <LessonIcon fill={hiabColors.greyPrimary} height={"2rem"} />
      case LessonType.Practice:
        return <PracticeIcon fill={hiabColors.greyPrimary} height={"2rem"} />
    }
    return <LessonIcon fill={hiabColors.greyPrimary} height={"2rem"} />
  }

  const navigateToTrainingResultHandle = (trainingReport: ITrainingReport) => {
    navigate(
      `../organization/${selectedUser?.organizationId}/user/${selectedUser?.id}/trainingreport/${trainingReport.id}`,
      { relative: "route" },
    )
  }

  const canNavigateToTrainingResult = (trainingReport: ITrainingReport): boolean => {
    return trainingReport.parameters.length > 0
  }

  return (
    <TableContainer>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Lesson</TableCell>
            <TableCell>Crane</TableCell>
            <TableCell>Accessory</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trainingReports
            .slice()
            .sort((x, y) => {
              var dateA = new Date(y.startDate).getTime()
              var dateB = new Date(x.startDate).getTime()
              return dateA > dateB ? 1 : -1
            })
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((trainingReport) => (
              <TableRow
                key={trainingReport.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                hover={canNavigateToTrainingResult(trainingReport)}
                onClick={() => navigateToTrainingResultHandle(trainingReport)}
              >
                <TableCell width={25}>{getIcon(trainingReport.lessonId)}</TableCell>
                <TableCell>{getLessonName(trainingReport.lessonId)}</TableCell>
                <TableCell>{getCraneParameter(trainingReport)}</TableCell>
                <TableCell>{getToolParameter(trainingReport)}</TableCell>
                <TableCell>
                  {`${getMinutesBetweenDates(
                    new Date(trainingReport.startDate),
                    new Date(trainingReport.endDate),
                  )} min`}
                </TableCell>
                <TableCell>{new Date(trainingReport.endDate).toLocaleString()}</TableCell>
                <TableCell>
                  {CompleteStatus[trainingReport.completeStatus]?.toString() ?? "Completed"}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25]}
        count={trainingReports.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  )
}
